import type { HeaderSettingsDto, LinkItemDto, NavItemDto, SurfaceTone } from '@/lib/sanity/dto';
import { stegaClean } from '@/lib/utils/stega';

export type HeaderCta = {
    tone: SurfaceTone;
    item: NavItemDto;
};

/** The configured action buttons, one per page tone. Both are rendered; CSS
 *  shows the one matching the page's tone (`header.css`). */
export function getHeaderCtas(header?: HeaderSettingsDto): HeaderCta[] {
    const ctas: HeaderCta[] = [];

    if (header?.cta) ctas.push({ tone: 'brand', item: header.cta });
    if (header?.perlaCta) ctas.push({ tone: 'perla', item: header.perlaCta });

    return ctas;
}

export function hasLinkTarget(item?: LinkItemDto | null): boolean {
    if (!item) return false;

    if (item.linkType === 'external') {
        const href = item.href ? stegaClean(item.href).trim() : '';
        return !!href;
    }

    const slug = item.page?.slug ? stegaClean(item.page.slug).trim() : '';
    const anchor = item.anchor ? stegaClean(item.anchor).trim() : '';

    return !!(slug || anchor);
}

export function resolveHref(item: LinkItemDto, queryParams?: Record<string, string>): string {
    if (item.linkType === 'external') {
        const href = item.href ? stegaClean(item.href).trim() : '';
        return href ?? '#';
    }

    const pageSlug = item.page?.slug ? stegaClean(item.page.slug).trim() : '';
    let base = pageSlug ? `/${pageSlug}` : '/';

    if (item.openInModal) {
        base = `/m${base}`;
    }

    if (queryParams && Object.keys(queryParams).length > 0) {
        const searchParams = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value) searchParams.append(key, value);
        });
        const queryString = searchParams.toString();
        if (queryString) {
            const separator = base.includes('?') ? '&' : '?';
            base = `${base}${separator}${queryString}`;
        }
    }

    const anchor = item.anchor ? stegaClean(item.anchor).trim() : '';
    if (anchor) {
        return `${base}#${anchor}`;
    }

    return base;
}

export function getAnchorId(href: string): string | null {
    const i = href.indexOf('#');
    return i === -1 ? null : href.slice(i + 1) || null;
}

/** Anchors for the scroll spy. A modal link's anchor selects a tab inside the
 *  dialog, not a section of the page, so it is left out. */
export function getAnchorIds(items: LinkItemDto[]): string[] {
    const ids = items
        .filter((item) => !item.openInModal)
        .map((item) => getAnchorId(resolveHref(item)))
        .filter((id): id is string => Boolean(id));

    return Array.from(new Set(ids));
}

/** Whether a nav item is active for the current location. Anchor links only
 *  track scroll position on their own base page; on sub-pages they never match. */
export function isNavItemActive(item: LinkItemDto, pathname: string, activeAnchorId: string): boolean {
    const basePath = resolveHref(item).split('#')[0];

    if (item.anchor) {
        return pathname === basePath && item.anchor === activeAnchorId;
    }

    return pathname === basePath;
}
